import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { ArrowLeft, IdentificationCard, Lock, Eye, EyeSlash, Scales, Bank, Flag, Star, SealCheck, Bird, Trophy } from "@phosphor-icons/react";
import { toast } from "sonner";
import ElectionsPreview from "../components/ElectionsPreview";
import ReceiptVerifier from "../components/ReceiptVerifier";
import MobileBrandBar from "../components/MobileBrandBar";
import { SalesInquiryButton } from "../components/SalesInquiryDialog";

const EMBLEM_TONES = {
    rose:    { bg: "bg-rose-500/15",    border: "border-rose-400/40",    text: "text-rose-200",    label: "text-rose-200/80" },
    amber:   { bg: "bg-amber-500/15",   border: "border-amber-400/40",   text: "text-amber-200",   label: "text-amber-200/80" },
    civic:   { bg: "bg-civic-500/20",   border: "border-civic-300/40",   text: "text-civic-100",   label: "text-civic-200/80" },
    emerald: { bg: "bg-emerald-500/15", border: "border-emerald-400/40", text: "text-emerald-200", label: "text-emerald-200/80" },
    sky:     { bg: "bg-sky-500/15",     border: "border-sky-400/40",     text: "text-sky-200",     label: "text-sky-200/80" },
    violet:  { bg: "bg-violet-500/15",  border: "border-violet-400/40",  text: "text-violet-200",  label: "text-violet-200/80" },
};

function Emblem({ icon: Icon, label, tone = "civic" }) {
    const t = EMBLEM_TONES[tone] || EMBLEM_TONES.civic;
    return (
        <div className="group flex flex-col items-center gap-1.5" data-testid={`emblem-${label}`}>
            <div className={`relative h-14 w-14 aspect-square grid place-items-center rounded-xl ${t.bg} border ${t.border} backdrop-blur-sm shadow-inner transition-transform group-hover:scale-105`}>
                <Icon size={26} weight="duotone" className={t.text} />
            </div>
            <span className={`text-[10px] font-bold uppercase tracking-widest ${t.label}`}>{label}</span>
        </div>
    );
}

export default function VoterLogin() {
    const { voterLogin, error } = useAuth();
    const nav = useNavigate();
    const [searchParams] = useSearchParams();
    const [voterId, setVoterId] = useState("");
    const [pin, setPin] = useState("");
    const [showPin, setShowPin] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [busy, setBusy] = useState(false);
    const receiptParam = (searchParams.get("receipt") || "").trim();
    const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
    useEffect(() => {
        const mq = window.matchMedia("(min-width: 1024px)");
        const on = (e) => setIsDesktop(e.matches);
        mq.addEventListener("change", on);
        return () => mq.removeEventListener("change", on);
    }, []);

    // Prefill Voter ID from ?voter_id=... — hit from the "E-VoteVerify.com"
    // hotlink and "Cast your ballot" CTA button inside Welcome / Reminder
    // emails so voters don't have to remember or retype their ID.
    useEffect(() => {
        const q = searchParams.get("voter_id");
        if (q && !voterId) setVoterId(q.trim());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const submit = async (e) => {
        e.preventDefault();
        if (pin.length < 4 || pin.length > 12) {
            toast.error("PIN or password must be 4–12 characters");
            return;
        }
        setBusy(true);
        const ok = await voterLogin(voterId, pin, rememberMe);
        setBusy(false);
        if (ok) { toast.success("Welcome to E-VoteVerify.com"); nav("/vote"); }
        else toast.error(error || "Login failed");
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-3 bg-white">
            {/* Mobile branding bar — keeps the app name + install CTA always visible on phones */}
            <MobileBrandBar subtitle="Verified Voter Ledger · Sign in to vote" />
            <div className="relative hidden lg:flex flex-col p-8 bg-civic-900 text-white overflow-hidden lg:col-span-2">
                {/* Decorative civic graphics */}
                <div className="absolute inset-0 dot-bg opacity-20" />
                {/* Stripe accents bottom — flag-inspired */}
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 opacity-[0.08]" aria-hidden="true">
                    <div className="h-full w-full" style={{
                        backgroundImage:
                            "repeating-linear-gradient(180deg, transparent 0 18px, rgba(255,255,255,0.6) 18px 22px, transparent 22px 40px)",
                    }} />
                </div>
                {/* Faint stars row */}
                <div className="pointer-events-none absolute top-24 right-10 flex flex-wrap gap-2 max-w-[220px] opacity-30" aria-hidden="true">
                    {Array.from({ length: 24 }).map((_, i) => (
                        <Star key={i} size={14} weight="fill" className="text-civic-200" />
                    ))}
                </div>
                {/* Scales of Justice — top-right large decorative */}
                <Scales
                    size={260}
                    weight="duotone"
                    className="pointer-events-none absolute -top-4 -right-6 text-civic-300 opacity-[0.10] rotate-[8deg]"
                    aria-hidden="true"
                />
                {/* Capitol/Bank silhouette — bottom-right */}
                <Bank
                    size={220}
                    weight="duotone"
                    className="pointer-events-none absolute -bottom-6 right-8 text-emerald-300 opacity-[0.08]"
                    aria-hidden="true"
                />
                {/* Flag — bottom-left small floating */}
                <Flag
                    size={140}
                    weight="duotone"
                    className="pointer-events-none absolute bottom-24 left-6 text-civic-300 opacity-[0.10] -rotate-6"
                    aria-hidden="true"
                />

                <div className="relative flex items-center justify-between mb-16">
                    <Link to="/about" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm" data-testid="back-home">
                        <ArrowLeft size={16} /> About E-VoteVerify.com
                    </Link>
                    <div className="hash-mono text-[10px] text-civic-200/70">sha256 / 0x00 → ∞</div>
                </div>

                {/* Welcome / value-prop block */}
                <div className="relative" data-testid="login-welcome">
                    <div className="flex items-center gap-2 text-civic-100/90 text-[11px] hash-mono uppercase tracking-widest">
                        <span>Accessible</span>
                        <span className="opacity-50">•</span>
                        <span>Secure</span>
                        <span className="opacity-50">•</span>
                        <span>Cost-Effective</span>
                        <span className="opacity-50">•</span>
                        <span>Transparent</span>
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                        <div className="h-10 w-10 rounded-md grid place-items-center bg-white/5 border border-white/10 ring-1 ring-civic-300/20" aria-hidden="true">
                            <Scales size={22} weight="duotone" className="text-emerald-300" />
                        </div>
                        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight leading-tight">
                            Welcome to the E-Vote Verify platform
                        </h2>
                    </div>
                    <p className="text-civic-100/80 text-sm leading-snug mt-2 max-w-2xl">
                        A <span className="font-semibold text-white">blockchain-secured</span> voting platform built for everyone — from registered voters in a state, county, or precinct, to a Family Group, the local PTA, or even organizations like the Boy Scouts. <span className="font-semibold text-emerald-300">Vote with security and surety.</span>
                    </p>

                    {/* Patriotic emblem strip — symbols of voting freedom */}
                    <div className="relative mt-8 grid grid-cols-6 gap-2 max-w-2xl" aria-hidden="true" data-testid="emblem-strip">
                        <Emblem icon={Flag} label="USA" tone="rose" />
                        <Emblem icon={Star} label="Texas" tone="amber" />
                        <Emblem icon={Bank} label="Capitol" tone="civic" />
                        <Emblem icon={SealCheck} label="Ballot" tone="emerald" />
                        <Emblem icon={Bird} label="Liberty" tone="sky" />
                        <Emblem icon={Trophy} label="Freedom" tone="violet" />
                    </div>

                    <Link to="/about#cryptography" className="mt-4 inline-flex items-center gap-1 text-[11px] text-civic-200 hover:text-white transition-colors hash-mono" data-testid="how-its-secured-link">
                        sha256 + bcrypt + a hash chain — see exactly how it works →
                    </Link>
                </div>

                {/* Elections preview — sits directly below the welcome block (no flex-1 spacer
                    so the cards don't get pushed to the very bottom of tall screens) */}
                <div className="relative mt-10 flex-1 min-h-0 overflow-y-auto pr-1 -mr-1" data-testid="login-elections-preview">
                    <ElectionsPreview />
                </div>

                {isDesktop && (
                    <div className="relative mt-6 max-w-2xl text-slate-900 rounded-lg bg-white" data-testid="login-receipt-verifier">
                        <ReceiptVerifier initialHash={receiptParam} />
                    </div>
                )}

                {/* Sales / Licensing CTA anchored at the very bottom of the About panel */}
                <div className="relative mt-6 pt-5 border-t border-white/10 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="min-w-0 flex-1">
                        <div className="overline text-civic-200/80">Running your own election?</div>
                        <p className="text-xs text-civic-100/80 leading-snug mt-1 max-w-md">
                            License E-VoteVerify for your county, HOA, board or organization — talk to our sales team.
                        </p>
                    </div>
                    <SalesInquiryButton
                        className="shrink-0 shadow-lg shadow-civic-900/40"
                        size="default"
                        label="Inquire about Sales / Licensing"
                        testId="voter-login-sales-inquiry-btn"
                    />
                </div>
            </div>

            <div className="flex flex-col justify-center px-5 sm:px-12 py-8 sm:py-12 max-w-xl mx-auto w-full">
                <Link to="/about" className="lg:hidden inline-flex items-center gap-2 text-slate-500 text-sm mb-6">
                    <ArrowLeft size={16} /> Back
                </Link>
                <Link to="/" className="block mb-8" data-testid="voter-login-logo">
                    <img
                        src="/brand-logo.png"
                        alt="E-VoteVerify.com — Secure · Transparent · Verified · Powered by Blockchain"
                        className="w-4/5 h-auto mx-auto"
                        loading="eager"
                        decoding="async"
                    />
                </Link>
                <div className="overline text-civic-600">Voter Portal</div>
                <h1 className="font-heading font-black text-3xl sm:text-4xl mt-2 text-slate-900 tracking-tighter">Sign in to vote</h1>
                <p className="mt-3 text-sm text-slate-600">Use the credentials issued by your election authority.</p>

                <form onSubmit={submit} className="mt-6 sm:mt-8 space-y-4 sm:space-y-5" data-testid="voter-login-form">
                    <div>
                        <Label htmlFor="vid" className="overline text-slate-500">Voter ID or Email</Label>
                        <div className="relative mt-2">
                            <IdentificationCard size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                                id="vid"
                                value={voterId}
                                onChange={(e) => setVoterId(e.target.value)}
                                placeholder="VOTER001 or jane@example.com"
                                className="pl-10 h-11 font-mono"
                                required
                                data-testid="voter-id-input"
                            />
                        </div>
                    </div>
                    <div>
                        <Label htmlFor="pin" className="overline text-slate-500">PIN or Password</Label>
                        <div className="relative mt-2">
                            <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                                id="pin"
                                type={showPin ? "text" : "password"}
                                value={pin}
                                onChange={(e) => setPin(e.target.value)}
                                placeholder="4–12 characters"
                                className="pl-10 pr-10 h-11 font-mono"
                                required
                                minLength={4}
                                maxLength={12}
                                autoComplete="current-password"
                                data-testid="voter-pin-input"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPin((s) => !s)}
                                aria-label={showPin ? "Hide PIN" : "Show PIN"}
                                title={showPin ? "Hide" : "Show"}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded text-slate-500 hover:text-civic-700 hover:bg-slate-100 transition-colors"
                                data-testid="voter-pin-toggle"
                            >
                                {showPin ? <EyeSlash size={18} weight="bold" /> : <Eye size={18} weight="bold" />}
                            </button>
                        </div>
                        <p className="mt-1.5 text-[11px] text-slate-500">4–12 characters. Letters, numbers, or symbols.</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="remember"
                            checked={rememberMe}
                            onCheckedChange={(v) => setRememberMe(!!v)}
                            data-testid="voter-remember-me"
                        />
                        <Label htmlFor="remember" className="text-sm text-slate-700 cursor-pointer select-none">
                            Remember me on this device
                        </Label>
                    </div>
                    <Button
                        type="submit"
                        disabled={busy}
                        className="w-full h-11 bg-civic-600 hover:bg-civic-700 text-white"
                        data-testid="voter-login-submit"
                    >
                        {busy ? "Verifying…" : "Sign in & continue"}
                    </Button>
                </form>

                <div className="hidden sm:flex mt-8 pt-6 border-t border-slate-200 items-center justify-between text-sm">
                    <span className="text-slate-500">Are you an election official?</span>
                    <Link to="/login/admin" className="text-civic-600 font-medium underline-offset-4 hover:underline" data-testid="switch-admin-link">
                        Admin login →
                    </Link>
                </div>

                {/* Marketing links — visible on both mobile and desktop, so a
                    voter arriving cold has one tap to the pillar page. */}
                <div className="mt-6 flex flex-wrap items-center justify-center sm:justify-start gap-x-4 gap-y-2 text-xs">
                    <Link to="/learn-more" className="text-civic-600 font-medium underline-offset-4 hover:underline" data-testid="voter-login-learn-more">
                        Learn more →
                    </Link>
                    <Link to="/features" className="text-civic-600 font-medium underline-offset-4 hover:underline" data-testid="voter-login-features">
                        Features
                    </Link>
                    <Link to="/explorer" className="text-slate-500 underline-offset-4 hover:underline hover:text-civic-600" data-testid="voter-login-explorer">
                        Chain explorer
                    </Link>
                    <Link to="/login/admin" className="sm:hidden text-slate-500 underline-offset-4 hover:underline hover:text-civic-600" data-testid="voter-login-admin-mobile">
                        Admin login
                    </Link>
                </div>

                {/* Mobile-only elections preview — desktop shows it inside the hero panel */}
                {!isDesktop && (
                    <div className="mt-8" data-testid="mobile-receipt-verifier">
                        <ReceiptVerifier compact initialHash={receiptParam} />
                    </div>
                )}

                <div className="lg:hidden mt-8" data-testid="mobile-elections-preview">
                    <div className="overline text-civic-600 mb-2">Active &amp; recent elections</div>
                    <ElectionsPreview theme="light" />
                </div>
            </div>
        </div>
    );
}
